import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Sun,
  Plus,
  Edit,
  Trash2,
  Search,
  Save,
  X,
  Loader2,
  Filter,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Layers,
} from "lucide-react";
import { usePermissions } from "@/hooks/usePermissions";
import { useAuth } from "@/hooks/useAuth";
import { useIsMobile } from "@/hooks/use-mobile";
import { ModuloFotovoltaico, CreateModuloFotovoltaico } from "@/types/modulos";
import { modulosService } from "@/services/modulosService";
import { toast } from "sonner";

export default function ModulosFotovoltaicos() {
  const permissions = usePermissions();
  const { user, session } = useAuth();
  const isMobile = useIsMobile();
  const [modulos, setModulos] = useState<ModuloFotovoltaico[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingModulo, setEditingModulo] = useState<ModuloFotovoltaico | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sortColumn, setSortColumn] = useState<string>('marca');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [filterOpen, setFilterOpen] = useState(false);
  const [appliedFilter, setAppliedFilter] = useState({ marca: '', tecnologia: '', potencia: '' });
  const [formData, setFormData] = useState<CreateModuloFotovoltaico>({
    marca: "",
    modelo: "",
    potencia: 0,
    tecnologia: "",
    dimensoes: { comprimento: 0, largura: 0, espessura: 0 },
    peso: 0,
    tipoCelula: "",
    potenciaMaxima: 0,
    tensaoCircuitoAberto: 0,
    tensaoPotenciaMaxima: 0,
    correnteCurtoCircuito: 0,
    correntePotenciaMaxima: 0,
    eficienciaModulo: 0,
    garantiaFabricacao: 0,
    garantiaProdutividade: 0,
    imagemUrl: null,
  });
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const imagemModuloInputRef = useRef<HTMLInputElement>(null);

  // Debug: Log de autenticação
  useEffect(() => {
    if (import.meta.env.DEV) {
      console.log('🔐 ModulosFotovoltaicos - Estado de autenticação:', {
        user: user?.id,
        email: user?.email,
        session: !!session,
        userRole: permissions.userRole,
        canManage: permissions.can('manage_modules'),
        sessionAccessToken: session?.access_token ? 'Presente' : 'Ausente',
        sessionRefreshToken: session?.refresh_token ? 'Presente' : 'Ausente',
        sessionExpiresAt: session?.expires_at,
        currentTime: Math.floor(Date.now() / 1000)
      });

      // Verificar se o token está expirado
      if (session?.expires_at) {
        const currentTime = Math.floor(Date.now() / 1000);
        const isExpired = currentTime > session.expires_at;
        console.log('⏰ Token expirado:', isExpired, {
          currentTime,
          expiresAt: session.expires_at,
          timeLeft: session.expires_at - currentTime
        });
      }
    }
  }, [user, session, permissions]);

  // Carregar módulos do Supabase
  useEffect(() => {
    loadModulos();
  }, []);

  const loadModulos = async () => {
    try {
      setIsLoading(true);
      const data = await modulosService.getAll();
      setModulos(data);
    } catch (error) {
      console.error('Erro ao carregar módulos:', error);
      toast.error('Erro ao carregar módulos fotovoltaicos');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: string, value: unknown) => {
    if (field.startsWith('dimensoes.')) {
      const dimField = field.split('.')[1];
      setFormData(prev => ({
        ...prev,
        dimensoes: {
          ...prev.dimensoes,
          [dimField]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Verificar se o usuário está autenticado
    if (!user || !session) {
      toast.error('Usuário não autenticado. Faça login para continuar.');
      return;
    }

    // Verificar se o usuário tem permissão (admin ou vendedor)
    if (permissions.userRole !== 'admin' && permissions.userRole !== 'vendedor') {
      toast.error('Acesso negado. Apenas administradores e vendedores podem gerenciar módulos fotovoltaicos.');
      return;
    }
    
    try {
      setIsSubmitting(true);

      if (editingModulo) {
        let imagemUrl = formData.imagemUrl ?? null;
        if (selectedImage) {
          try {
            imagemUrl = await modulosService.uploadImagem(editingModulo.id, selectedImage);
          } catch (uploadErr) {
            console.error("Erro ao fazer upload da imagem:", uploadErr);
            toast.error("Erro ao fazer upload da imagem");
            return;
          }
        }
        const updatedModulo = await modulosService.update(editingModulo.id, {
          ...formData,
          imagemUrl,
        });
        setModulos((prev) =>
          prev.map((m) => (m.id === editingModulo.id ? updatedModulo : m))
        );
        toast.success("Módulo atualizado com sucesso!");
      } else {
        const created = await modulosService.create({ ...formData, imagemUrl: null });
        if (selectedImage) {
          try {
            const imgUrl = await modulosService.uploadImagem(created.id, selectedImage);
            const updated = await modulosService.update(created.id, { imagemUrl: imgUrl });
            setModulos((prev) => prev.map((m) => (m.id === created.id ? updated : m)));
            toast.success("Módulo criado com sucesso!");
          } catch (uploadErr) {
            console.error("Erro ao fazer upload da imagem:", uploadErr);
            toast.error("Módulo criado, mas houve erro ao fazer upload da imagem");
            setModulos((prev) => [...prev, created]);
          }
        } else {
          setModulos((prev) => [...prev, created]);
          toast.success("Módulo criado com sucesso!");
        }
      }

      resetForm();
      setIsDialogOpen(false);
    } catch (error) {
      console.error('Erro ao salvar módulo:', error);
      toast.error('Erro ao salvar módulo fotovoltaico');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (modulo: ModuloFotovoltaico) => {
    // Verificar se o usuário tem permissão (admin ou vendedor)
    if (permissions.userRole !== 'admin' && permissions.userRole !== 'vendedor') {
      toast.error('Acesso negado. Apenas administradores e vendedores podem editar módulos fotovoltaicos.');
      return;
    }

    setEditingModulo(modulo);
    setFormData({
      marca: modulo.marca,
      modelo: modulo.modelo,
      potencia: modulo.potencia,
      tecnologia: modulo.tecnologia,
      dimensoes: modulo.dimensoes,
      peso: modulo.peso,
      tipoCelula: modulo.tipoCelula,
      potenciaMaxima: modulo.potenciaMaxima,
      tensaoCircuitoAberto: modulo.tensaoCircuitoAberto,
      tensaoPotenciaMaxima: modulo.tensaoPotenciaMaxima,
      correnteCurtoCircuito: modulo.correnteCurtoCircuito,
      correntePotenciaMaxima: modulo.correntePotenciaMaxima,
      eficienciaModulo: modulo.eficienciaModulo,
      garantiaFabricacao: modulo.garantiaFabricacao,
      garantiaProdutividade: modulo.garantiaProdutividade,
      imagemUrl: modulo.imagemUrl ?? null,
    });
    setSelectedImage(null);
    setImagePreview(modulo.imagemUrl || null);
    setIsDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    // Verificar se o usuário tem permissão (admin ou vendedor)
    if (permissions.userRole !== 'admin' && permissions.userRole !== 'vendedor') {
      toast.error('Acesso negado. Apenas administradores e vendedores podem excluir módulos fotovoltaicos.');
      return;
    }

    if (confirm("Tem certeza que deseja excluir este módulo?")) {
      try {
        await modulosService.delete(id);
        setModulos(prev => prev.filter(m => m.id !== id));
        toast.success('Módulo excluído com sucesso!');
      } catch (error) {
        console.error('Erro ao excluir módulo:', error);
        toast.error('Erro ao excluir módulo fotovoltaico');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      marca: "",
      modelo: "",
      potencia: 0,
      tecnologia: "",
      dimensoes: { comprimento: 0, largura: 0, espessura: 0 },
      peso: 0,
      tipoCelula: "",
      potenciaMaxima: 0,
      tensaoCircuitoAberto: 0,
      tensaoPotenciaMaxima: 0,
      correnteCurtoCircuito: 0,
      correntePotenciaMaxima: 0,
      eficienciaModulo: 0,
      garantiaFabricacao: 0,
      garantiaProdutividade: 0,
      imagemUrl: null,
    });
    setEditingModulo(null);
    setSelectedImage(null);
    setImagePreview(null);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"];
      if (!allowedTypes.includes(file.type)) {
        toast.error("Tipo de arquivo não permitido. Use JPEG, PNG, GIF ou WebP.");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Arquivo muito grande. Tamanho máximo: 5MB.");
        return;
      }
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    setFormData((prev) => ({ ...prev, imagemUrl: null }));
    if (imagemModuloInputRef.current) {
      imagemModuloInputRef.current.value = "";
    }
  };

  const applyFilters = () => {
    setFilterOpen(false);
  };

  const clearFilters = () => {
    setAppliedFilter({ marca: '', tecnologia: '', potencia: '' });
    setFilterOpen(false);
  };

  const filtersApplied = Object.values(appliedFilter).some(val => val !== '' && val !== 'todos');

  const uniqueMarcas = [...new Set(modulos.map(m => m.marca || '').filter(marca => marca !== ''))];
  const uniqueTecnologias = [...new Set(modulos.map(m => m.tecnologia || '').filter(tecnologia => tecnologia !== ''))];
  const uniquePotencias = [...new Set(modulos.map(m => m.potencia || 0).filter(potencia => potencia !== 0))];

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const sortedModulos = [...modulos].sort((a, b) => {
    if (!sortColumn) return 0;

    let aVal: unknown;
    let bVal: unknown;

    switch (sortColumn) {
      case 'marca':
        aVal = a.marca || '';
        bVal = b.marca || '';
        return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      case 'modelo':
        aVal = a.modelo || '';
        bVal = b.modelo || '';
        return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      case 'potencia':
        aVal = a.potencia || 0;
        bVal = b.potencia || 0;
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      case 'tecnologia':
        aVal = a.tecnologia || '';
        bVal = b.tecnologia || '';
        return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      case 'eficienciaModulo':
        aVal = a.eficienciaModulo || 0;
        bVal = b.eficienciaModulo || 0;
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      case 'peso':
        aVal = a.peso || 0;
        bVal = b.peso || 0;
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      default:
        return 0;
    }
  });

  const filteredModulos = sortedModulos.filter(modulo => {
    const marca = (modulo.marca || '').toLowerCase();
    const modelo = (modulo.modelo || '').toLowerCase();
    const tecnologia = (modulo.tecnologia || '').toLowerCase();
    const potencia = modulo.potencia || 0;

    const matchesSearch = appliedSearch === '' || 
                          marca.includes(appliedSearch.toLowerCase()) ||
                          modelo.includes(appliedSearch.toLowerCase()) ||
                          tecnologia.includes(appliedSearch.toLowerCase());

    const matchesFilter = (appliedFilter.marca === '' || appliedFilter.marca === 'todos' || marca === appliedFilter.marca.toLowerCase()) &&
                          (appliedFilter.tecnologia === '' || appliedFilter.tecnologia === 'todos' || tecnologia === appliedFilter.tecnologia.toLowerCase()) &&
                          (appliedFilter.potencia === '' || appliedFilter.potencia === 'todos' || potencia === Number(appliedFilter.potencia));

    return matchesSearch && matchesFilter;
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
        <span className="ml-2">Carregando módulos...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      {/* Header */}
      <div className={`flex ${isMobile ? 'flex-col' : 'items-center justify-between'} gap-4`}>
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground">Módulos Fotovoltaicos</h1>
          <p className="text-sm md:text-base text-muted-foreground">
            {(permissions.userRole === 'admin' || permissions.userRole === 'vendedor')
              ? 'Gerencie o catálogo de módulos fotovoltaicos disponíveis'
              : 'Visualize o catálogo de módulos fotovoltaicos disponíveis (apenas leitura)'
            }
          </p>
        </div>
        <div className={`flex ${isMobile ? 'flex-wrap' : 'items-center'} gap-2`}>
          <Badge variant="outline">
            {modulos.length} módulos cadastrados
          </Badge>
          {permissions.userRole !== 'admin' && permissions.userRole !== 'vendedor' && (
            <Badge variant="secondary">
              Modo Visualização
            </Badge>
          )}
        </div>
      </div>

      {/* Search and Actions */}
      <div className={`flex ${isMobile ? 'flex-col' : 'items-center justify-between'} gap-3`}>
        <form
          className={`flex ${isMobile ? 'flex-col' : 'flex-row'} gap-2 flex-1`}
          onSubmit={(e) => {
            e.preventDefault();
            setAppliedSearch(searchTerm);
          }}
        >
          <div className={`relative ${isMobile ? 'w-full' : 'w-96'}`}>
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              placeholder="Buscar por marca, modelo ou tecnologia..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className={`flex ${isMobile ? 'flex-row' : ''} gap-2`}>
            <Button type="submit" variant="outline" className={isMobile ? "flex-1" : ""}>
              <Search className="h-4 w-4 mr-2" /> Buscar
            </Button>
            <Button
              type="button"
              variant={filtersApplied ? "default" : "outline"}
              onClick={() => setFilterOpen(true)}
              className={isMobile ? "flex-1" : ""}
            >
              <Filter className="h-4 w-4 mr-2" /> Filtros
            </Button>
          </div>
        </form>
        
        <div className={`flex ${isMobile ? 'w-full' : 'items-center'} gap-2`}>
          {(permissions.userRole === 'admin' || permissions.userRole === 'vendedor') ? (
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={() => resetForm()} className={isMobile ? "w-full" : ""}>
                  <Plus className="h-4 w-4 mr-2" />
                  Adicionar Módulo
                </Button>
              </DialogTrigger>
              <DialogContent className={`${isMobile ? 'max-w-[95vw]' : 'max-w-4xl'} max-h-[90vh] overflow-y-auto`}>
                <DialogHeader>
                  <DialogTitle>
                    {editingModulo ? "Editar Módulo" : "Adicionar Novo Módulo"}
                  </DialogTitle>
                  <DialogDescription>
                    Preencha as informações técnicas do módulo fotovoltaico
                  </DialogDescription>
                </DialogHeader>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Informações Básicas */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Informações Básicas</CardTitle>
                    </CardHeader>
                    <CardContent className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="marca">Marca *</Label>
                        <Input
                          id="marca"
                          value={formData.marca}
                          onChange={(e) => handleInputChange("marca", e.target.value)}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="modelo">Modelo *</Label>
                        <Input
                          id="modelo"
                          value={formData.modelo}
                          onChange={(e) => handleInputChange("modelo", e.target.value)}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="potencia">Potência (W) *</Label>
                        <Input
                          id="potencia"
                          type="number"
                          value={formData.potencia}
                          onChange={(e) => handleInputChange("potencia", Number(e.target.value))}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="tecnologia">Tecnologia *</Label>
                        <Input
                          id="tecnologia"
                          value={formData.tecnologia}
                          onChange={(e) => handleInputChange("tecnologia", e.target.value)}
                          required
                        />
                      </div>
                      <div className="space-y-2 col-span-2">
                        <Label htmlFor="imagemModulo">Imagem do módulo</Label>
                        <div className="flex flex-wrap items-center gap-2">
                          <Input
                            ref={imagemModuloInputRef}
                            id="imagemModulo"
                            type="file"
                            accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                            onChange={handleImageChange}
                            className="cursor-pointer max-w-md flex-1 min-w-[200px]"
                          />
                          {(imagePreview || selectedImage) && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={handleRemoveImage}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Remover imagem
                            </Button>
                          )}
                        </div>
                        {imagePreview && (
                          <div className="mt-2">
                            <img
                              src={imagePreview}
                              alt="Pré-visualização"
                              className="w-32 h-32 object-cover rounded border"
                            />
                          </div>
                        )}
                        <p className="text-sm text-muted-foreground">
                          Formatos aceitos: JPEG, PNG, GIF, WebP. Tamanho máximo: 5MB
                        </p>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Dimensões e Peso */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Dimensões e Peso</CardTitle>
                    </CardHeader>
                    <CardContent className="grid grid-cols-4 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="comprimento">Comprimento (mm) *</Label>
                        <Input
                          id="comprimento"
                          type="number"
                          value={formData.dimensoes.comprimento}
                          onChange={(e) => handleInputChange("dimensoes.comprimento", Number(e.target.value))}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="largura">Largura (mm) *</Label>
                        <Input
                          id="largura"
                          type="number"
                          value={formData.dimensoes.largura}
                          onChange={(e) => handleInputChange("dimensoes.largura", Number(e.target.value))}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="espessura">Espessura (mm) *</Label>
                        <Input
                          id="espessura"
                          type="number"
                          value={formData.dimensoes.espessura}
                          onChange={(e) => handleInputChange("dimensoes.espessura", Number(e.target.value))}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="peso">Peso (kg) *</Label>
                        <Input
                          id="peso"
                          type="number"
                          step="0.1"
                          value={formData.peso}
                          onChange={(e) => handleInputChange("peso", Number(e.target.value))}
                          required
                        />
                      </div>
                    </CardContent>
                  </Card>

                  {/* Características Técnicas */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Características Técnicas</CardTitle>
                    </CardHeader>
                    <CardContent className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="tipoCelula">Tipo de Célula *</Label>
                        <Input
                          id="tipoCelula"
                          value={formData.tipoCelula}
                          onChange={(e) => handleInputChange("tipoCelula", e.target.value)}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="potenciaMaxima">Potência Máxima (W) *</Label>
                        <Input
                          id="potenciaMaxima"
                          type="number"
                          value={formData.potenciaMaxima}
                          onChange={(e) => handleInputChange("potenciaMaxima", Number(e.target.value))}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="tensaoCircuitoAberto">Tensão Circuito Aberto (Voc) *</Label>
                        <Input
                          id="tensaoCircuitoAberto"
                          type="number"
                          step="0.1"
                          value={formData.tensaoCircuitoAberto}
                          onChange={(e) => handleInputChange("tensaoCircuitoAberto", Number(e.target.value))}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="tensaoPotenciaMaxima">Tensão Potência Máxima (Vmp) *</Label>
                        <Input
                          id="tensaoPotenciaMaxima"
                          type="number"
                          step="0.1"
                          value={formData.tensaoPotenciaMaxima}
                          onChange={(e) => handleInputChange("tensaoPotenciaMaxima", Number(e.target.value))}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="correnteCurtoCircuito">Corrente Curto-Circuito (Isc) *</Label>
                        <Input
                          id="correnteCurtoCircuito"
                          type="number"
                          step="0.01"
                          value={formData.correnteCurtoCircuito}
                          onChange={(e) => handleInputChange("correnteCurtoCircuito", Number(e.target.value))}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="correntePotenciaMaxima">Corrente Potência Máxima (Imp) *</Label>
                        <Input
                          id="correntePotenciaMaxima"
                          type="number"
                          step="0.01"
                          value={formData.correntePotenciaMaxima}
                          onChange={(e) => handleInputChange("correntePotenciaMaxima", Number(e.target.value))}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="eficienciaModulo">Eficiência do Módulo (%) *</Label>
                        <Input
                          id="eficienciaModulo"
                          type="number"
                          step="0.1"
                          value={formData.eficienciaModulo}
                          onChange={(e) => handleInputChange("eficienciaModulo", Number(e.target.value))}
                          required
                        />
                      </div>
                    </CardContent>
                  </Card>

                  {/* Garantias */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Garantias</CardTitle>
                    </CardHeader>
                    <CardContent className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="garantiaFabricacao">Garantia de Fabricação (anos) *</Label>
                        <Input
                          id="garantiaFabricacao"
                          type="number"
                          value={formData.garantiaFabricacao}
                          onChange={(e) => handleInputChange("garantiaFabricacao", Number(e.target.value))}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="garantiaProdutividade">Garantia de Produtividade (anos) *</Label>
                        <Input
                          id="garantiaProdutividade"
                          type="number"
                          value={formData.garantiaProdutividade}
                          onChange={(e) => handleInputChange("garantiaProdutividade", Number(e.target.value))}
                          required
                        />
                      </div>
                    </CardContent>
                  </Card>

                  {/* Actions */}
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                      <X className="h-4 w-4 mr-2" />
                      Cancelar
                    </Button>
                    <Button type="submit" disabled={isSubmitting}>
                      {isSubmitting ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Save className="h-4 w-4 mr-2" />
                      )}
                      {editingModulo ? "Atualizar" : "Salvar"}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          ) : (
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="text-sm">
                {permissions.userRole ? `Role: ${permissions.userRole}` : 'Não autenticado'}
              </Badge>
              <span className="text-sm text-muted-foreground">
                Apenas administradores e vendedores podem gerenciar módulos
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Table or Cards */}
      <Card>
        <CardHeader>
          <CardTitle>Catálogo de Módulos</CardTitle>
          <CardDescription>
            Lista de todos os módulos fotovoltaicos cadastrados no sistema
          </CardDescription>
        </CardHeader>
        <CardContent>
          {filteredModulos.length === 0 ? (
            <p className="text-center text-muted-foreground">Nenhum módulo encontrado.</p>
          ) : isMobile ? (
            // Vista de cards para mobile
            <div className="space-y-4">
              {filteredModulos.map((modulo) => (
                <Card key={modulo.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="space-y-3">
                      <div className="flex gap-3 items-start">
                        {modulo.imagemUrl ? (
                          <img
                            src={modulo.imagemUrl}
                            alt={`${modulo.marca} ${modulo.modelo}`}
                            className="w-20 h-20 object-cover rounded shrink-0"
                          />
                        ) : (
                          <div className="w-20 h-20 bg-muted rounded flex items-center justify-center shrink-0">
                            <Layers className="h-8 w-8 text-muted-foreground" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0 flex justify-between gap-2">
                          <div>
                            <h3 className="font-semibold text-lg">{modulo.marca}</h3>
                            <p className="text-sm text-muted-foreground truncate">{modulo.modelo}</p>
                          </div>
                          <Badge variant="outline" className="shrink-0">
                            {modulo.potencia}W
                          </Badge>
                        </div>
                      </div>
                      
                      {/* Informações do módulo */}
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">Tecnologia:</span>
                          <span className="font-medium">{modulo.tecnologia}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">Eficiência:</span>
                          <span className="font-medium">{modulo.eficienciaModulo}%</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">Dimensões:</span>
                          <span className="font-medium text-xs">
                            {modulo.dimensoes.comprimento}×{modulo.dimensoes.largura}×{modulo.dimensoes.espessura}mm
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">Peso:</span>
                          <span className="font-medium">{modulo.peso}kg</span>
                        </div>
                      </div>
                      
                      {/* Botões de ação */}
                      {(permissions.userRole === 'admin' || permissions.userRole === 'vendedor') ? (
                        <div className="flex gap-2 pt-2">
                          <Button
                            variant="outline"
                            className="flex-1"
                            size="sm"
                            onClick={() => handleEdit(modulo)}
                          >
                            <Edit className="h-4 w-4 mr-2" />
                            Editar
                          </Button>
                          <Button
                            variant="outline"
                            className="flex-1"
                            size="sm"
                            onClick={() => handleDelete(modulo.id)}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Excluir
                          </Button>
                        </div>
                      ) : (
                        <p className="text-xs text-muted-foreground text-center pt-2">
                          Apenas administradores e vendedores podem editar
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            // Vista de tabela para desktop
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Imagem</TableHead>
                  <TableHead className={`cursor-pointer ${sortColumn === 'marca' ? 'text-primary' : ''}`} onClick={() => handleSort('marca')}>
                    Marca
                    {sortColumn === 'marca' ? (
                      sortDirection === 'asc' ? <ArrowUp className="ml-2 h-4 w-4 inline" /> : <ArrowDown className="ml-2 h-4 w-4 inline" />
                    ) : (
                      <ArrowUpDown className="ml-2 h-4 w-4 inline" />
                    )}
                  </TableHead>
                  <TableHead className={`cursor-pointer ${sortColumn === 'modelo' ? 'text-primary' : ''}`} onClick={() => handleSort('modelo')}>
                    Modelo
                    {sortColumn === 'modelo' ? (
                      sortDirection === 'asc' ? <ArrowUp className="ml-2 h-4 w-4 inline" /> : <ArrowDown className="ml-2 h-4 w-4 inline" />
                    ) : (
                      <ArrowUpDown className="ml-2 h-4 w-4 inline" />
                    )}
                  </TableHead>
                  <TableHead className={`cursor-pointer ${sortColumn === 'potencia' ? 'text-primary' : ''}`} onClick={() => handleSort('potencia')}>
                    Potência
                    {sortColumn === 'potencia' ? (
                      sortDirection === 'asc' ? <ArrowUp className="ml-2 h-4 w-4 inline" /> : <ArrowDown className="ml-2 h-4 w-4 inline" />
                    ) : (
                      <ArrowUpDown className="ml-2 h-4 w-4 inline" />
                    )}
                  </TableHead>
                  <TableHead className={`cursor-pointer ${sortColumn === 'tecnologia' ? 'text-primary' : ''}`} onClick={() => handleSort('tecnologia')}>
                    Tecnologia
                    {sortColumn === 'tecnologia' ? (
                      sortDirection === 'asc' ? <ArrowUp className="ml-2 h-4 w-4 inline" /> : <ArrowDown className="ml-2 h-4 w-4 inline" />
                    ) : (
                      <ArrowUpDown className="ml-2 h-4 w-4 inline" />
                    )}
                  </TableHead>
                  <TableHead>Dimensões</TableHead>
                  <TableHead className={`cursor-pointer ${sortColumn === 'eficienciaModulo' ? 'text-primary' : ''}`} onClick={() => handleSort('eficienciaModulo')}>
                    Eficiência
                    {sortColumn === 'eficienciaModulo' ? (
                      sortDirection === 'asc' ? <ArrowUp className="ml-2 h-4 w-4 inline" /> : <ArrowDown className="ml-2 h-4 w-4 inline" />
                    ) : (
                      <ArrowUpDown className="ml-2 h-4 w-4 inline" />
                    )}
                  </TableHead>
                  <TableHead>Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredModulos.map((modulo) => (
                  <TableRow key={modulo.id}>
                    <TableCell>
                      {modulo.imagemUrl ? (
                        <img
                          src={modulo.imagemUrl}
                          alt={`${modulo.marca} ${modulo.modelo}`}
                          className="w-16 h-16 object-cover rounded"
                        />
                      ) : (
                        <div className="w-16 h-16 bg-muted rounded flex items-center justify-center">
                          <Layers className="h-6 w-6 text-muted-foreground" />
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="font-medium">{modulo.marca}</TableCell>
                    <TableCell>{modulo.modelo}</TableCell>
                    <TableCell>{modulo.potencia}W</TableCell>
                    <TableCell>{modulo.tecnologia}</TableCell>
                    <TableCell>
                      {modulo.dimensoes.comprimento}×{modulo.dimensoes.largura}×{modulo.dimensoes.espessura}mm
                    </TableCell>
                    <TableCell>{modulo.eficienciaModulo}%</TableCell>
                    <TableCell>
                      {(permissions.userRole === 'admin' || permissions.userRole === 'vendedor') ? (
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEdit(modulo)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete(modulo.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground">
                          Apenas administradores e vendedores podem editar
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Modal de Filtros */}
      <Dialog open={filterOpen} onOpenChange={setFilterOpen}>
        <DialogContent className={isMobile ? 'max-w-[95vw]' : ''}>
          <DialogHeader>
            <DialogTitle>Filtros</DialogTitle>
            <DialogDescription>
              Filtre a lista de módulos por marca, tecnologia ou potência.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-sm font-medium">Marca</Label>
              <Select value={appliedFilter.marca} onValueChange={(val) => setAppliedFilter(prev => ({ ...prev, marca: val }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione marca" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todas</SelectItem>
                  {uniqueMarcas.sort().map(marca => (
                    <SelectItem key={marca} value={marca.toLowerCase()}>{marca}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium">Tecnologia</Label>
              <Select value={appliedFilter.tecnologia} onValueChange={(val) => setAppliedFilter(prev => ({ ...prev, tecnologia: val }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione tecnologia" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todas</SelectItem>
                  {uniqueTecnologias.sort().map(tecnologia => (
                    <SelectItem key={tecnologia} value={tecnologia.toLowerCase()}>{tecnologia}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium">Potência</Label>
              <Select value={appliedFilter.potencia} onValueChange={(val) => setAppliedFilter(prev => ({ ...prev, potencia: val }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione potência" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todas</SelectItem>
                  {uniquePotencias.sort((a, b) => a - b).map(potencia => (
                    <SelectItem key={potencia} value={potencia.toString()}>{potencia}W</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={clearFilters}>Limpar Filtros</Button>
              <Button onClick={applyFilters}>Aplicar Filtros</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
